import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MediaElementContext } from '../../context/MediaElementContext';
import { INTEGRATIONS_PLATFORM, ZOOM } from '../../../types/CallSourceTypes';
import { getIntegrationsAuthenticatedFile } from '../../clients/getIntegrationsAuthenticatedFile';
import { getZoomRecordingFile } from '../../clients/getZoomRecordingFile';
import { MediaPlaybackControlsContext } from '../../context/MediaPlaybackControlsContext';
import { logMediaPlayerErrorMetric } from '../../utils/logMediaPlayerErrorMetric';
import { setRefreshInterval } from '../../utils/refreshInterval';
import { useZoomExternalAccountId } from '../../hooks/_internal/useZoomExternalAccountId';
import { getParsedPlayerStatuses } from '../../utils/getParsedPlayerStatuses';
import { getNetworkErrorResult } from '../../utils/getNetworkErrorResult';
import { jsx as _jsx } from "react/jsx-runtime";
const THIRTY_MINUTES_IN_MILLISECONDS = 30 * 60 * 1000;
export const withIntegrationsRecordingUrlRefresh = Component => {
  return props => {
    const {
      mediaRef,
      engagementId,
      externalAccountId: externalAccountIdFromContext,
      setErrorStatus,
      setMaybeRequiresAuthUpdate,
      maybeRequiresAuthUpdate,
      refetchedMediaUrl,
      setRefetchedMediaUrl,
      mediaType
    } = useContext(MediaElementContext);
    const {
      status
    } = useContext(MediaPlaybackControlsContext);
    const {
      isError
    } = getParsedPlayerStatuses(status);
    const interval = useRef(undefined);
    const currentRecordingUrl = useRef(props.recordingUrl || null);
    const [refreshedRecordingUrl, setRefreshedRecordingUrl] = useState(null);
    const externalAccountId = useZoomExternalAccountId({
      engagementId,
      externalAccountId: externalAccountIdFromContext,
      source: props.source
    });

    // We need to keep track of the current recording url so we can check if it's changed.
    // If it's changed we may need to shutdown an async process that's trying to refresh the url.
    useEffect(() => {
      currentRecordingUrl.current = props.recordingUrl || null;
      return () => {
        currentRecordingUrl.current = null;
      };
    }, [props.recordingUrl]);

    // If an error occurs we don't want to keep trying to refresh the url.
    // Refetching the media occurs once on audio error after 30 minutes have passed,
    // then every 30 minutes after that.
    const canRefreshRecordingUrl = useMemo(() => !!(props.recordingUrl && !isError && (props.source === INTEGRATIONS_PLATFORM && engagementId || props.source === ZOOM && externalAccountId && engagementId)), [engagementId, externalAccountId, props.recordingUrl, props.source, isError]);
    useEffect(() => {
      if (!canRefreshRecordingUrl || !props.recordingUrl) {
        setRefreshedRecordingUrl(null);
        return () => {};
      }
      interval.current = setRefreshInterval(() => {
        setMaybeRequiresAuthUpdate(true);
      }, THIRTY_MINUTES_IN_MILLISECONDS);

      // There's no perfect way to know when this component was mounted and when the data was loaded into the UI.
      // performance.now() has bugs in some browsers on sleep and we can't rely on it.
      // As a workaround we can just set the initial value when the src or source changes and canRefreshRecordingUrl is true.
      // This way if it's been more than 30 minutes on mount we can potentially refresh the url on playback if an error occurs.
      setMaybeRequiresAuthUpdate(true);
      setRefreshedRecordingUrl(null);
      setRefetchedMediaUrl(false);
      return () => {
        if (typeof interval.current === 'function') {
          interval.current();
          interval.current = undefined;
        }
      };
    }, [canRefreshRecordingUrl, props.recordingUrl, setMaybeRequiresAuthUpdate, setRefetchedMediaUrl]);
    useEffect(() => {
      // If the player is in an error state we don't want to keep setting the state.
      if (isError) {
        if (typeof interval.current === 'function') {
          interval.current();
          interval.current = undefined;
        }
        setMaybeRequiresAuthUpdate(false);
        setRefreshedRecordingUrl(null);
        setRefetchedMediaUrl(false);
      }
    }, [isError, setMaybeRequiresAuthUpdate, setRefetchedMediaUrl]);
    const maybeRefreshRecordingUrl = useCallback(async () => {
      let nextRecordingUrl = null;
      try {
        if (props.source === ZOOM) {
          nextRecordingUrl = await getZoomRecordingFile({
            engagementId: engagementId,
            externalAccountId: externalAccountId,
            mediaType
          });
        } else {
          const res = await getIntegrationsAuthenticatedFile(engagementId);
          nextRecordingUrl = res.recordingUrl || res.videoRecordingUrl || null;
        }
        if (!nextRecordingUrl) {
          throw new Error('No recording url found');
        }

        // The recording url has changed so we don't need to refresh it.
        if (props.recordingUrl !== currentRecordingUrl.current) return;
        setRefreshedRecordingUrl(nextRecordingUrl);
        setRefetchedMediaUrl(true);
      } catch (error) {
        // The recording url has changed so we don't need to refresh it.
        if (props.recordingUrl !== currentRecordingUrl.current) return;
        setMaybeRequiresAuthUpdate(false);
        const {
          errorType,
          networkStatus
        } = getNetworkErrorResult(error, props.source === ZOOM);
        setErrorStatus(errorType);
        logMediaPlayerErrorMetric({
          errorType,
          requestedRecordingUrlUpdate: true,
          source: props.source,
          status: networkStatus
        });
      }
    }, [engagementId, externalAccountId, mediaType, props.recordingUrl, props.source, setErrorStatus, setMaybeRequiresAuthUpdate, setRefetchedMediaUrl]);
    useEffect(() => {
      if (refetchedMediaUrl && maybeRequiresAuthUpdate) {
        var _mediaRef$current;
        // refetchedMediaUrl is set to false when the recording is loaded in useHTMLMediaElementEvents.tsx
        setMaybeRequiresAuthUpdate(false);
        (_mediaRef$current = mediaRef.current) === null || _mediaRef$current === void 0 || _mediaRef$current.load();
      }
    }, [maybeRequiresAuthUpdate, mediaRef, refetchedMediaUrl, setMaybeRequiresAuthUpdate]);
    return /*#__PURE__*/_jsx(Component, Object.assign({}, props, {
      recordingUrl: refreshedRecordingUrl || props.recordingUrl,
      refreshRecordingUrl: canRefreshRecordingUrl && maybeRequiresAuthUpdate ? maybeRefreshRecordingUrl : undefined
    }));
  };
};